import {
    Component,
    ElementRef,
    Input,
    Output,
    ViewChild,
    EventEmitter,
    AfterViewInit,
    OnChanges,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { CustomerPrivacyCategory } from 'layout/customer-privacy/mask-data';

export type MultiselectAutocompleteItem = {
    name: string;
    value: string;
};

@Component({
    selector: 'ce-multiselect-autocomplete',
    templateUrl: 'multiselect-autocomplete.component.html',
    styleUrls: ['multiselect-autocomplete.component.scss'],
    // We need to manipulate chip-list-wrapper styling and therefore
    // must go without viewcapsulation.
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class MultiselectAutocompleteComponent implements OnChanges, AfterViewInit {
    filteredValues: { [value: string]: true } = {};

    private _selectedValues: string[] = [];

    get selectedValues(): string[] {
        return this._selectedValues;
    }
    set selectedValues(value: string[]) {
        if (value.length === 0) {
            this._selectedValues = [];
        } else {
            this._selectedValues = [
                ...value,
                ...this._selectedValues.filter((selectedValue) => !this.filteredValues[selectedValue]),
            ];
        }
        this._emitSelectedChanged();
        this._filterSelectedItems(this.lastFilterValue);
    }

    lastFilterValue: string | null = '';

    @Input('display') display: 'checkbox' | 'chip' = 'checkbox';
    @Input('label') label: string;
    @Input('placeholder') placeholder = '';
    @Input('autoActiveFirstOption') autoActiveFirstOption = false;
    @Input('disabled') disabled = false;
    @Input('items') items: MultiselectAutocompleteItem[] = [];
    @Input('selected') set setSelectedValues(ids: string[]) {
        this._selectedValues = ids;
    }
    @Input() customerPrivacyFilter?: CustomerPrivacyCategory = 'randomNumber';

    @Output() selectedChanged = new EventEmitter<MultiselectAutocompleteItem[]>();

    @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;
    @ViewChild('filterInput') filterInput: ElementRef<HTMLInputElement>;
    filterText: string;

    ngAfterViewInit(): void {
        this._filterSelectedItems(this.lastFilterValue);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.items || changes.selected) {
            this._filterSelectedItems(this.lastFilterValue);
        }
    }

    reset(): void {
        this._resetSelections();
    }

    isSelectedItem(lookupItem: MultiselectAutocompleteItem) {
        return !!this._selectedValues.includes(lookupItem.value);
    }

    filterInputKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.selectAllFiltered();
        } else {
            const value = (event.target as HTMLInputElement)?.value || '';
            this.filterText = value.trim();
            this._filterSelectedItems(value);
        }
    }

    selectAllFiltered() {
        this.selectedValues = [
            ...this.selectedValues,
            ...Object.keys(this.filteredValues).filter((item) => !this.selectedValues.includes(item)),
        ];
    }

    clearSelectedClick() {
        this._resetSelections();
    }

    getNameForValue(value: string) {
        return this.items.find((item) => item.value === value)?.name || '';
    }

    selectOpenChanged(open: boolean) {
        // Set focus on filter input when select options list
        // is opened
        if (open && this.filterInput) {
            this.filterInput.nativeElement.focus();
        }
    }

    private _filterSelectedItems(filterValue: string): void {
        const lcFilterValue = (filterValue || '').toLowerCase();
        this.lastFilterValue = lcFilterValue;

        if (lcFilterValue) {
            this.filteredValues = this.items
                .map((item) => {
                    const isMatch = (item.name || '').toLowerCase().includes(lcFilterValue);
                    return isMatch || this.isSelectedItem(item) ? item.value : null;
                })
                .filter(Boolean)
                .reduce((acc, v) => {
                    acc[v] = true;
                    return acc;
                }, {});
        } else {
            this.filteredValues = this.items
                .map((item) => item.value)
                .reduce((acc, v) => {
                    acc[v] = true;
                    return acc;
                }, {});
        }
    }

    private _emitSelectedChanged() {
        this.selectedChanged.emit(this.items.filter((item) => this._selectedValues.includes(item.value)));
    }

    private _resetSelections() {
        this.selectedValues = [];
    }

    trackByFn(idx: number, item: MultiselectAutocompleteItem) {
        return item?.value;
    }
}
